import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Phone, ShieldCheck, ArrowRight, Lock } from "lucide-react";
import {
  FloatingLabelInput, GradientButton, AuthSplitLeft, AuthHeader,
  AuthFooterNote, SecurityStrip, ContinueAsBanner,
} from "../components/AuthUI";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.58 — Team Login is now ONE-STEP: just enter mobile and Login.
 * OTP requirement removed per user request. Backend `/auth/team-login`
 * issues JWT directly when mobile matches an active team member.
 */
export default function TeamLogin() {
  const { teamLogin, user, isTeamMember } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  usePageTitle("Team Console Access | HouseEntry");

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    const digits = mobile.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) {
      toast.error("Enter a valid 10-digit mobile");
      return;
    }
    setBusy(true);
    try {
      const resp = await teamLogin(digits);
      toast.success("Welcome back!");
      // v2.71.48 — Route post-login by sub_role so field-agents land on
      // /team/field and callers on /team/workflow (their PERSONA_META.home).
      const subRole = resp?.user?.sub_role || resp?.sub_role;
      const home = subRole === "agent" ? "/team/field" : "/team/workflow";
      nav(home, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-screen bg-zinc-50" data-testid="team-login-page">
      <AuthSplitLeft
        eyebrow="HouseEntry · Internal Team Console"
        heading="Quick access for the team that closes deals."
        sub="Streamlined login for callers, lead handlers and internal operations."
        quote="Single-tap mobile login keeps our calling workflow tight."
        attribution="Lead Operations · HouseEntry HQ"
      />

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <div className="auth-blobs lg:hidden opacity-30" />
        <div className="w-full max-w-md relative">
          <AuthHeader showBack backTo="/" />

          <div className="auth-glass rounded-2xl p-7 sm:p-8" data-testid="team-login-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15803D] inline-flex items-center gap-1.5">
                <ShieldCheck size={12} /> Internal Console
              </div>
              <SecurityStrip />
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-zinc-900">Team Console Access</h1>
            <p className="text-[14px] text-zinc-600 mt-1.5 mb-6">
              Mobile number daalkar seedha login karein — admin ne aapka mobile register kar diya hoga.
            </p>

            {user && isTeamMember && (
              <ContinueAsBanner user={user} onContinue={() => nav("/team", { replace: true })} testid="team-continue-as-banner" />
            )}

            <form onSubmit={handleLogin} className="space-y-4" data-testid="team-login-form">
              <FloatingLabelInput
                id="team-mobile"
                type="tel"
                label="Mobile number"
                icon={Phone}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 15))}
                inputMode="numeric"
                maxLength={15}
                required
                autoFocus
                testid="team-login-mobile-input"
                hint="+91 prefix is auto-applied. Enter the 10-digit number registered with admin."
              />
              <GradientButton type="submit" loading={busy} testid="team-login-submit-btn" icon={ArrowRight}>
                Login
              </GradientButton>
            </form>

            <div className="mt-6 pt-5 border-t border-zinc-200 text-center text-[12px] text-zinc-500 inline-flex items-center justify-center gap-1.5 w-full">
              <Lock size={11} /> Not a team member?{" "}
              <a href="/login" className="text-[#0055FF] font-bold hover:underline">Sign in with email →</a>
            </div>
          </div>

          <div className="mt-6">
            <AuthFooterNote />
          </div>
        </div>
      </div>
    </div>
  );
}
